import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import {
  betaInterestAdminService,
  type BetaInterestItem,
  type BetaInterestStatus,
} from '../services/betaInterestAdminService'

const PAGE_SIZE = 25
const QUERY_KEY = ['admin', 'beta-interest'] as const

type StatusFilter = BetaInterestStatus | 'all'

const FILTERS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'Todas' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'activated', label: 'Activadas' },
]

const dateFormatter = new Intl.DateTimeFormat('es-VE', { dateStyle: 'medium', timeStyle: 'short' })
const formatDate = (iso: string) => dateFormatter.format(new Date(iso))

interface RowProps {
  item: BetaInterestItem
  busy: boolean
  onActivate: (item: BetaInterestItem) => void
  onRevert: (item: BetaInterestItem) => void
}

function BetaInterestRow({ item, busy, onActivate, onRevert }: RowProps) {
  const activated = item.status === 'activated'
  return (
    <tr className="border-t border-slate-100">
      <td className="px-3 py-2 font-medium text-slate-900">{item.name}</td>
      <td className="px-3 py-2">
        <a href={`mailto:${item.email}`} className="text-blue-700 underline">
          {item.email}
        </a>
      </td>
      <td className="px-3 py-2 text-slate-700">{item.whatsapp ? `+${item.whatsapp}` : '—'}</td>
      <td className="px-3 py-2 text-slate-600">{formatDate(item.createdAt)}</td>
      <td className="px-3 py-2">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            activated ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
          }`}
        >
          {activated ? 'Activada' : 'Pendiente'}
        </span>
        {item.notifiedAt && (
          <span className="mt-1 block text-xs text-slate-500">Avisada {formatDate(item.notifiedAt)}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {activated ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onRevert(item)}
            className="text-sm text-slate-600 underline disabled:opacity-50"
          >
            Volver a pendiente
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => onActivate(item)}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            Activar y avisar
          </button>
        )}
      </td>
    </tr>
  )
}

const AdminBetaInterestPage: React.FC = () => {
  const { token } = useAuth()
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<StatusFilter>('pending')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [confirming, setConfirming] = useState<BetaInterestItem | null>(null)

  const query = useQuery({
    queryKey: [...QUERY_KEY, status, search, page],
    queryFn: () =>
      betaInterestAdminService.list(
        { status: status === 'all' ? undefined : status, search, page, limit: PAGE_SIZE },
        token
      ),
  })

  const mutation = useMutation({
    mutationFn: (vars: { item: BetaInterestItem; status: BetaInterestStatus; notify: boolean }) =>
      betaInterestAdminService.setStatus(vars.item.id, vars.status, vars.notify, token),
    onSuccess: (result, vars) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      setConfirming(null)
      if (vars.status === 'pending') {
        addToast({ type: 'info', title: 'Reserva actualizada', message: 'Volvió a pendiente.' })
      } else if (result.emailSent) {
        addToast({ type: 'success', title: 'Reserva activada', message: `Se avisó a ${vars.item.email}.` })
      } else {
        addToast({
          type: 'warning',
          title: 'Activada, sin aviso',
          message: 'El envío de correo no está configurado en el servidor: avisa a la persona por otro medio.',
        })
      }
    },
    onError: (err: unknown) =>
      addToast({
        type: 'error',
        title: 'No se pudo actualizar',
        message: err instanceof Error ? err.message : 'Intenta de nuevo.',
      }),
  })

  const data = query.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  const changeFilter = (next: StatusFilter) => {
    setStatus(next)
    setPage(1)
  }

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold text-slate-900">Reservas de la beta</h1>
      <p className="mt-1 text-sm text-slate-600">
        Personas que dejaron sus datos en el home. El correo es el medio para avisarles que su lugar está activo.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div role="group" aria-label="Filtrar por estado" className="flex overflow-hidden rounded-md border border-slate-300">
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              aria-pressed={status === filter.value}
              onClick={() => changeFilter(filter.value)}
              className={`px-3 py-1.5 text-sm ${
                status === filter.value ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {filter.label}
              {data && filter.value !== 'all' && ` (${data.counts[filter.value]})`}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <span className="sr-only">Buscar por nombre, correo o WhatsApp</span>
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder="Buscar nombre, correo o WhatsApp"
            className="w-72 max-w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
        </label>
      </div>

      {confirming && (
        <div role="alertdialog" aria-label="Confirmar activación" className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="text-amber-900">
            Se activará la reserva de <strong>{confirming.name}</strong> y se enviará un correo a{' '}
            <strong>{confirming.email}</strong> con el enlace para crear su cuenta.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ item: confirming, status: 'activated', notify: true })}
              className="rounded-md bg-emerald-600 px-3 py-1.5 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Enviando…' : 'Activar y enviar correo'}
            </button>
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ item: confirming, status: 'activated', notify: false })}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-slate-700 disabled:opacity-50"
            >
              Activar sin enviar
            </button>
            <button type="button" onClick={() => setConfirming(null)} className="px-3 py-1.5 text-slate-600 underline">
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 overflow-x-auto rounded-md border border-slate-200 bg-white">
        {query.isLoading ? (
          <p className="p-6 text-sm text-slate-500">Cargando reservas…</p>
        ) : query.isError ? (
          <div className="p-6 text-sm text-red-700" role="alert">
            {query.error instanceof Error ? query.error.message : 'No se pudo cargar la lista.'}{' '}
            <button type="button" onClick={() => void query.refetch()} className="underline">
              Reintentar
            </button>
          </div>
        ) : !data || data.items.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            {search || status !== 'all' ? 'No hay reservas con ese filtro.' : 'Aún no hay reservas.'}
          </p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th scope="col" className="px-3 py-2">Nombre</th>
                <th scope="col" className="px-3 py-2">Correo</th>
                <th scope="col" className="px-3 py-2">WhatsApp</th>
                <th scope="col" className="px-3 py-2">Se anotó</th>
                <th scope="col" className="px-3 py-2">Estado</th>
                <th scope="col" className="px-3 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <BetaInterestRow
                  key={item.id}
                  item={item}
                  busy={mutation.isPending}
                  onActivate={setConfirming}
                  onRevert={(target) => mutation.mutate({ item: target, status: 'pending', notify: false })}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
          <span>
            Página {page} de {totalPages} · {data.total} reservas
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded border px-3 py-1 disabled:opacity-50">
              Anterior
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border px-3 py-1 disabled:opacity-50">
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminBetaInterestPage
